// src/firebase.js
/* eslint-disable no-console */
import { initializeApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import {
  getFirestore,
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  limit as qLimit,
  runTransaction,
  writeBatch,
  serverTimestamp,
  updateDoc,
  setDoc,
  increment,
  documentId,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";


/** ---------------------------------------------------------------------
 * Firebase config
 * ------------------------------------------------------------------- */
const firebaseConfig = {
  apiKey: "AIzaSyAIgkIwGbGu7-XGYdA7Lqc_Tg5TBgrh5oE",
  authDomain: "unionhall-1673d.firebaseapp.com",
  projectId: "unionhall-1673d",
  storageBucket: "unionhall-1673d.firebasestorage.app", // ✅ new-style bucket
  messagingSenderId: "535863646507",
  appId: "1:535863646507:web:0897f6f2dcde4c7dbdcd6e",
  measurementId: "G-7PXPQYHY42",
};

const app = initializeApp(firebaseConfig);

// Core SDKs
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Cloud Functions (callables)
export const functions = getFunctions(app, "us-central1");

// Google Geocoding + Places Autocomplete callables
export const geocodeListingAddressFn = httpsCallable(
  functions,
  "geocodeListingAddress"
);
export const placesAutocompleteFn = httpsCallable(
  functions,
  "placesAutocomplete"
);


// Persist auth across reloads (no API changes elsewhere)
setPersistence(auth, browserLocalPersistence).catch(() => {});

// Dev log
const isDev = process.env.NODE_ENV !== "production";
if (isDev) {
  console.log("[Firebase] Storage bucket:", firebaseConfig.storageBucket);
}

/* ----------------------------------------------------------------------
 * Phone auth testing helper (web):
 * Enable Firebase Auth "Phone numbers for testing" on localhost ONLY.
 * This disables reCAPTCHA app verification for the current device,
 * so NEVER run it in production hosts.
 * -------------------------------------------------------------------- */
try {
  if (typeof window !== "undefined") {
    const DEV_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);
    if (DEV_HOSTS.has(window.location.hostname)) {
      // Allows console-configured test numbers to work without real SMS.
      // Do NOT ship this for production domains.
      // Ref: auth.settings.appVerificationDisabledForTesting (Web)
      // Safe no-op if the property is unavailable.
      // eslint-disable-next-line no-unused-expressions
      auth.settings && (auth.settings.appVerificationDisabledForTesting = true);
      console.log("[Firebase] Phone test numbers enabled (appVerificationDisabledForTesting).");
    }
  }
} catch {}

/** =========================== Pricing (MVP) =========================== **/
export const SERVICE_FEE_USD = 25;

const CITY_MULTIPLIERS = [
  { test: /(new york|nyc|san francisco|sf|los angeles|la|boston|seattle|Washington)/i, mult: 1.35 },
  { test: /(chicago|miami|san diego|denver|austin|portland|oakland|san jose)/i, mult: 1.2 },
  { test: /(dallas|houston|atlanta|philadelphia|phoenix|minneapolis|charlotte)/i, mult: 1.1 },
];

function basePerGuest(capacity) {
  const c = Number(capacity || 0);
  if (c <= 8) return 18;
  if (c <= 15) return 16;
  if (c <= 30) return 14;
  if (c <= 50) return 12;
  if (c <= 80) return 10;
  return 9;
}

export function suggestDayRate({ city = "", stateCode = "", capacity = 10 }) {
  const mult =
    CITY_MULTIPLIERS.find((m) => m.test.test(`${city} ${stateCode}`))?.mult || 1.0;
  const raw = Math.round(capacity * basePerGuest(capacity) * mult);
  const clamped = Math.max(100, Math.min(1500, raw));
  return Math.round(clamped / 5) * 5;
}

/** =============================== Utils =============================== **/
export function pad2(n) {
  return String(n).padStart(2, "0");
}
export function ymd(d) {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}`;
}
export function addDays(d, n) {
  const x = new Date(d instanceof Date ? d : new Date(d));
  x.setDate(x.getDate() + n);
  return x;
}
export function addMonths(d, n) {
  const x = new Date(d instanceof Date ? d : new Date(d));
  x.setMonth(x.getMonth() + 1 * n);
  return x;
}
export function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
export function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
// Given a UI "end" (inclusive checkout date), produce the exclusive upper bound for queries
export function endExclusiveFromEndDate(endDateYYYYMMDD) {
  const [Y, M, D] = String(endDateYYYYMMDD || "").split("-").map(Number);
  if (!Y || !M || !D) return endDateYYYYMMDD;
  return ymd(addDays(new Date(Y, M - 1, D), 1)); // add 1 day
}
function parseYYYYMMDD(s) {
  const [Y, M, D] = String(s || "").split("-").map(Number);
  if (!Y || !M || !D) return null;
  return new Date(Y, M - 1, D);
}
export function* iterDatesYYYYMMDD(startInclusive, endExclusive) {
  let d = parseYYYYMMDD(startInclusive);
  const end = parseYYYYMMDD(endExclusive);
  if (!d || !end) return;
  while (d < end) {
    yield ymd(d);
    d = addDays(d, 1);
  }
}
export function randomGroupId(prefix = "grp") {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}${Math.random()
    .toString(36)
    .slice(2, 6)}`;
}

/** ============================ Normalizers ============================ **/
// Accepts string[], {key: boolean}, or comma-delimited string; returns clean, deduped string[]
function normalizeStringList(input, limit = 200) {
  let arr = [];
  if (Array.isArray(input)) {
    arr = input.map((x) => String(x || "").trim());
  } else if (input && typeof input === "object") {
    arr = Object.keys(input).filter((k) => !!input[k]).map((k) => String(k).trim());
  } else if (typeof input === "string") {
    arr = input.split(",").map((s) => s.trim());
  }
  const clean = arr.filter(Boolean);
  return Array.from(new Set(clean)).slice(0, limit);
}

/** ============================ Mappers =============================== **/
function toListing(snap) {
  const d = snap.data() || {};
  const bookingMode = String(d.bookingMode || "day").toLowerCase();
  const stateCode = d.stateCode || d.state || "";

  // Back-compat: capacity used by older UI; prefer maxGuestsDay when present
  const maxGuestsDay =
    Number.isFinite(Number(d.maxGuestsDay)) ? Number(d.maxGuestsDay) : null;
  const maxGuestsOvernight =
    Number.isFinite(Number(d.maxGuestsOvernight)) ? Number(d.maxGuestsOvernight) : null;

  const capacity =
    Number.isFinite(Number(d.capacity))
      ? Number(d.capacity)
      : Number.isFinite(Number(maxGuestsDay))
      ? Number(maxGuestsDay)
      : Number.isFinite(Number(maxGuestsOvernight))
      ? Number(maxGuestsOvernight)
      : 0;

  // Normalize prices/hours per mode so UI can reliably check nulls
  const dayPrice =
    bookingMode === "overnight"
      ? null
      : typeof d.dayPrice === "number"
      ? d.dayPrice
      : typeof d.dayRate === "number"
      ? d.dayRate
      : 0;

  const dayUseStartHour =
    bookingMode === "overnight"
      ? null
      : Number.isFinite(Number(d.dayUseStartHour))
      ? Number(d.dayUseStartHour)
      : null;

  const dayUseEndHour =
    bookingMode === "overnight"
      ? null
      : Number.isFinite(Number(d.dayUseEndHour))
      ? Number(d.dayUseEndHour)
      : null;

  const nightlyPrice =
    bookingMode === "day"
      ? null
      : Number.isFinite(Number(d.nightlyPrice))
      ? Number(d.nightlyPrice)
      : null;

  const minNights =
    bookingMode === "day"
      ? null
      : Number.isFinite(Number(d.minNights))
      ? Number(d.minNights)
      : null;

  const maxNights =
    bookingMode === "day"
      ? null
      : Number.isFinite(Number(d.maxNights))
      ? Number(d.maxNights)
      : null;

  const checkInHour =
    bookingMode === "day"
      ? null
      : Number.isFinite(Number(d.checkInHour))
      ? Number(d.checkInHour)
      : null;

  const checkOutHour =
    bookingMode === "day"
      ? null
      : Number.isFinite(Number(d.checkOutHour))
      ? Number(d.checkOutHour)
      : null;

  // Normalize areas/amenities from array | map | string -> array
  const areas = normalizeStringList(d.areas, 100);
  const amenities = normalizeStringList(d.amenities, 200);

  // Strongly-recommended arrays
  const goodForEventsTypes = normalizeStringList(d.goodForEventsTypes, 60);

  // Booking approval model:
  // - Prefer stored instantBook
  // - Else infer: approvalWindowHours present => Request to Book
  const instantBook =
    typeof d.instantBook === "boolean"
      ? d.instantBook
      : Number.isFinite(Number(d.approvalWindowHours))
      ? false
      : true;

  const approvalWindowHours =
    instantBook ? null : Number.isFinite(Number(d.approvalWindowHours)) ? Number(d.approvalWindowHours) : null;

  return {
    id: snap.id,

    ownerUid: d.ownerUid || "",
    ownerEmail: d.ownerEmail || null,

    title: d.title || "",
    summary: d.summary || "",

    // Back-compat + new capacities
    capacity,
    maxGuestsDay: maxGuestsDay ?? capacity ?? 0,
    maxGuestsOvernight: maxGuestsOvernight ?? 0,

    // Address fields
    address: d.address || "",
    city: d.city || "",
    stateCode,

    postalCode: d.addressPostalCode || d.postalCode || "",
    countryCode: d.addressCountryCode || d.countryCode || "US",
    county: d.addressCounty || d.county || "",
    formattedAddress: d.formattedAddress || "",

    // Optional normalized address extras (CreateListing sends these)
    addressStreet1: d.addressStreet1 || null,
    addressStreet2: d.addressStreet2 || null,
    normalizedAddress: d.normalizedAddress || null,

    locationLat: typeof d.locationLat === "number" ? d.locationLat : null,
    locationLng: typeof d.locationLng === "number" ? d.locationLng : null,
    locationCitySlug: d.locationCitySlug || "",
    locationMetroKey: d.locationMetroKey || "",

    // NEW: Must-haves
    propertyType: d.propertyType || "",
    bedrooms: Number.isFinite(Number(d.bedrooms)) ? Number(d.bedrooms) : 0,
    beds: Number.isFinite(Number(d.beds)) ? Number(d.beds) : 0,
    bathrooms: Number.isFinite(Number(d.bathrooms)) ? Number(d.bathrooms) : 0,

    // Space typing
    spaceType: d.spaceType || "entire_place",
    spaceName: d.spaceName || null,
    entranceType: d.entranceType || "shared",
    areas,
    amenities,

    // NEW: Strongly-recommended fields
    parkingSpotsOnsite:
      d.parkingSpotsOnsite === null || d.parkingSpotsOnsite === undefined
        ? null
        : Number.isFinite(Number(d.parkingSpotsOnsite))
        ? Number(d.parkingSpotsOnsite)
        : null,
    parkingNotes: d.parkingNotes || null,
    suitableForLoudMusic:
      typeof d.suitableForLoudMusic === "boolean" ? d.suitableForLoudMusic : null,
    quietHoursStart: d.quietHoursStart ?? null,
    quietHoursEnd: d.quietHoursEnd ?? null,
    sharedWalls: typeof d.sharedWalls === "boolean" ? d.sharedWalls : null,
    goodForEventsTypes,
    suitableForChildren: d.suitableForChildren || "",
    suitableForInfants:
      typeof d.suitableForInfants === "boolean" ? d.suitableForInfants : null,
    petsAllowed: typeof d.petsAllowed === "boolean" ? d.petsAllowed : null,
    hostLivesOnsite: d.hostLivesOnsite || "",
    isPrimaryResidence:
      typeof d.isPrimaryResidence === "boolean" ? d.isPrimaryResidence : null,

    // Booking
    bookingMode,
    dayPrice,
    dayUseStartHour,
    dayUseEndHour,
    nightlyPrice,
    minNights,
    maxNights,
    checkInHour,
    checkOutHour,

    // NEW: approval model
    instantBook,
    approvalWindowHours,

    // Optional structured fields from CreateListing (safe passthrough)
    bookingRules: d.bookingRules || null,
    thingsToKnow: d.thingsToKnow || null,
    pricingPolicy: d.pricingPolicy || null,
    guestDisplayDayPrice:
      d.guestDisplayDayPrice === null || d.guestDisplayDayPrice === undefined
        ? null
        : Number(d.guestDisplayDayPrice),
    guestDisplayNightlyPrice:
      d.guestDisplayNightlyPrice === null || d.guestDisplayNightlyPrice === undefined
        ? null
        : Number(d.guestDisplayNightlyPrice),

    rules: d.rules || "",

    photos: Array.isArray(d.photos) ? d.photos : [],
    status: d.status || "draft",

    createdAt: d.createdAt ?? null,
    updatedAt: d.updatedAt ?? null,
    submittedAt: d.submittedAt ?? null,
    publishedAt: d.publishedAt ?? null,
    reviewedAt: d.reviewedAt ?? null,
    reviewedBy: d.reviewedBy ?? null,
    rejectionReason: d.rejectionReason ?? "",
  };
}


/** ============================ Errors ================================= **/
export function friendlyFirebaseError(err) {
  const code = err?.code || "";
  const map = {
    "storage/unauthorized": "You don’t have permission to upload to Storage.",
    "storage/canceled": "Upload canceled.",
    "storage/quota-exceeded": "Storage quota exceeded.",
    "storage/retry-limit-exceeded": "Network unstable—upload retry limit exceeded.",
    "storage/invalid-argument": "Invalid upload. Try a different file type/size.",
    "permission-denied": "Permission denied.",
  };
  return map[code] || err?.message || "Sorry—something went wrong while uploading.";
}

/** ====================== Auth-ready (for Storage) ===================== **/
async function ensureAuthReady() {
  if (auth.currentUser) return auth.currentUser;
  await new Promise((resolve) => {
    const off = onAuthStateChanged(auth, () => {
      off();
      resolve();
    });
  });
  return auth.currentUser;
}

/** =========================== Upload helpers ========================== **/
// Upload one file while reporting aggregate progress over N files
async function uploadOneWithAggregateProgress({
  storageRef,
  file,
  i,
  total,
  onProgress,
}) {
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, {
      contentType: file.type || "application/octet-stream",
      cacheControl: "public, max-age=31536000, immutable",
    });
    task.on(
      "state_changed",
      (snap) => {
        const perFile = snap.totalBytes ? snap.bytesTransferred / snap.totalBytes : 1;
        const agg = ((i + perFile) / total) * 100;
        onProgress?.(Math.max(0, Math.min(100, Math.round(agg))));
      },
      reject,
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve({ url, bytes: task.snapshot.totalBytes });
      }
    );
  });
}

// Upload one listing image, aggregating by bytes (for large files)
async function uploadListingImage({
  ownerUid,
  file,
  baseUploaded,
  totalBytes,
  onProgress,
}) {
  const safeName = (file?.name || "photo").replace(/[^\w.-]+/g, "-").slice(0, 100);
  const key = `listings/${ownerUid}/${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}-${safeName}`;
  const storageRef = ref(storage, key);

  return await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, {
      contentType: file.type || "image/jpeg",
      cacheControl: "public, max-age=31536000, immutable",
      customMetadata: { uploadedBy: ownerUid },
    });
    task.on(
      "state_changed",
      (snap) => {
        const pct = Math.round(
          ((baseUploaded + snap.bytesTransferred) / Math.max(1, totalBytes)) * 100
        );
        onProgress?.(Math.min(100, Math.max(0, pct)));
      },
      reject,
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve({ url, bytes: task.snapshot.totalBytes });
      }
    );
  });
}

/** ============================ Listings API =========================== **/
export async function publishListing({
  ownerUid,
  ownerEmail,
  title,
  summary,

  // ✅ NEW: CreateListing uses these now
  maxGuestsDay = null,
  maxGuestsOvernight = null,

  // back-compat (older callers might still pass capacity)
  capacity = null,

  address,
  city,
  stateCode,

  // optional normalized / geo fields (from geocodeListingAddress OR CreateListing payload)
  postalCode = null,
  countryCode = "US",
  county = null,
  locationLat = null,
  locationLng = null,
  locationCitySlug = null,
  locationMetroKey = null,
  formattedAddress = null,

  // extra address pieces (CreateListing sends these)
  addressStreet1 = null,
  addressStreet2 = null,
  normalizedAddress = null,

  // ✅ NEW: Must-haves
  propertyType = "",
  bedrooms = 0,
  beds = 0,
  bathrooms = 0,

  // property/space typing
  spaceType = "entire_place",
  spaceName = null,
  entranceType = "shared",
  areas = [],
  amenities = [],

  // ✅ NEW: Strongly-recommended
  parkingSpotsOnsite = null,
  parkingNotes = null,
  suitableForLoudMusic = null,
  quietHoursStart = null,
  quietHoursEnd = null,
  sharedWalls = null,
  goodForEventsTypes = [],
  suitableForChildren = "",
  suitableForInfants = null,
  petsAllowed = null,
  hostLivesOnsite = "",
  isPrimaryResidence = null,

  // Daytime
  dayPrice,
  dayUseStartHour = null,
  dayUseEndHour = null,

  // Booking mode + overnight fields
  bookingMode = "day", // "day" | "overnight" | "hybrid"
  nightlyPrice = null,
  minNights = null,
  maxNights = null,
  checkInHour = null,
  checkOutHour = null,

  // ✅ NEW: Approval model
  instantBook = true,
  approvalWindowHours = null,

  // ✅ NEW: Optional structured fields (CreateListing sends these; safe to store)
  bookingRules = null,
  thingsToKnow = null,
  pricingPolicy = null,
  guestDisplayDayPrice = null,
  guestDisplayNightlyPrice = null,

  // Legacy rules
  rules,

  files,
  onProgress,
}) {
  if (!ownerUid) throw new Error("Missing ownerUid");
  await ensureAuthReady();

  const safeFiles = Array.isArray(files) ? files : [];
  const totalBytes = safeFiles.reduce((sum, f) => sum + (f?.size || 0), 0) || 1;

  let uploadedSoFar = 0;
  const urls = [];
  for (const f of safeFiles) {
    const { url, bytes } = await uploadListingImage({
      ownerUid,
      file: f,
      baseUploaded: uploadedSoFar,
      totalBytes,
      onProgress,
    });
    urls.push(url);
    uploadedSoFar += bytes;
  }
  onProgress?.(100);

  // sanitize arrays
  const cleanAreas = normalizeStringList(areas, 100);
  const cleanAmenities = normalizeStringList(amenities, 200);
  const cleanGoodForEvents = normalizeStringList(goodForEventsTypes, 60);

  const mode = String(bookingMode || "day").toLowerCase();

  // ✅ capacity back-compat: prefer maxGuestsDay (CreateListing)
  const cap =
    Number.isFinite(Number(maxGuestsDay))
      ? Number(maxGuestsDay)
      : Number.isFinite(Number(capacity))
      ? Number(capacity)
      : Number.isFinite(Number(maxGuestsOvernight))
      ? Number(maxGuestsOvernight)
      : 0;

  // ✅ approval rule: if instant book, window must be null
  const ib = typeof instantBook === "boolean" ? instantBook : true;
  const awh =
    ib ? null : Number.isFinite(Number(approvalWindowHours)) ? Number(approvalWindowHours) : null;

  // Features map fallback (CreateListing already sends it, but don’t depend on it)
  const featuresFallback = Object.fromEntries(cleanAmenities.map((k) => [k, true]));
  const safeFeatures =
    thingsToKnow && typeof thingsToKnow === "object" ? null : null; // no-op; keep structure minimal

  const payload = {
    ownerUid,
    ownerEmail: ownerEmail || null,

    title: title || "",
    summary: summary || "",

    // ✅ NEW capacities
    maxGuestsDay: Number.isFinite(Number(maxGuestsDay)) ? Number(maxGuestsDay) : cap,
    maxGuestsOvernight: Number.isFinite(Number(maxGuestsOvernight)) ? Number(maxGuestsOvernight) : 0,

    // back-compat
    capacity: cap,

    address: address || "",
    city: city || "",
    stateCode: stateCode || "",

    // normalized / geo details (safe to be null)
    addressPostalCode: postalCode || null,
    addressCountryCode: countryCode || "US",
    addressCounty: county || null,
    formattedAddress: formattedAddress || null,
    locationLat: typeof locationLat === "number" ? locationLat : null,
    locationLng: typeof locationLng === "number" ? locationLng : null,
    locationCitySlug: locationCitySlug || null,
    locationMetroKey: locationMetroKey || null,

    // extra address pieces
    addressStreet1: addressStreet1 || null,
    addressStreet2: addressStreet2 || null,
    normalizedAddress: normalizedAddress || null,

    // ✅ NEW must-haves
    propertyType: propertyType || "",
    bedrooms: Number.isFinite(Number(bedrooms)) ? Number(bedrooms) : 0,
    beds: Number.isFinite(Number(beds)) ? Number(beds) : 0,
    bathrooms: Number.isFinite(Number(bathrooms)) ? Number(bathrooms) : 0,

    // property/space typing
    spaceType: spaceType || "entire_place",
    spaceName: (spaceName || "").trim() || null,
    entranceType: entranceType || "shared",
    areas: cleanAreas,
    amenities: cleanAmenities,

    // ✅ NEW strongly-recommended
    parkingSpotsOnsite:
      parkingSpotsOnsite === null || parkingSpotsOnsite === undefined
        ? null
        : Number.isFinite(Number(parkingSpotsOnsite))
        ? Number(parkingSpotsOnsite)
        : null,
    parkingNotes: (parkingNotes || "").trim() || null,
    suitableForLoudMusic: typeof suitableForLoudMusic === "boolean" ? suitableForLoudMusic : null,
    quietHoursStart: quietHoursStart ?? null,
    quietHoursEnd: quietHoursEnd ?? null,
    sharedWalls: typeof sharedWalls === "boolean" ? sharedWalls : null,
    goodForEventsTypes: cleanGoodForEvents,
    suitableForChildren: suitableForChildren || "",
    suitableForInfants: typeof suitableForInfants === "boolean" ? suitableForInfants : null,
    petsAllowed: typeof petsAllowed === "boolean" ? petsAllowed : null,
    hostLivesOnsite: hostLivesOnsite || "",
    isPrimaryResidence: typeof isPrimaryResidence === "boolean" ? isPrimaryResidence : null,

    bookingMode: mode,
    rules: rules || "",
    photos: urls,

    // ✅ approval model
    instantBook: ib,
    approvalWindowHours: awh,

    // ✅ optional structured passthroughs
    bookingRules: bookingRules && typeof bookingRules === "object" ? bookingRules : null,
    thingsToKnow: thingsToKnow && typeof thingsToKnow === "object" ? thingsToKnow : null,
    pricingPolicy: pricingPolicy && typeof pricingPolicy === "object" ? pricingPolicy : null,
    guestDisplayDayPrice:
      guestDisplayDayPrice === null || guestDisplayDayPrice === undefined
        ? null
        : Number(guestDisplayDayPrice),
    guestDisplayNightlyPrice:
      guestDisplayNightlyPrice === null || guestDisplayNightlyPrice === undefined
        ? null
        : Number(guestDisplayNightlyPrice),

    status: "pending",
    submittedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  // Daytime fields if applicable
  if (mode !== "overnight") {
    payload.dayPrice = dayPrice === null || dayPrice === undefined ? null : Number(dayPrice || 0);
    payload.dayUseStartHour =
      dayUseStartHour === null || dayUseStartHour === "" ? null : Number(dayUseStartHour);
    payload.dayUseEndHour =
      dayUseEndHour === null || dayUseEndHour === "" ? null : Number(dayUseEndHour);
  } else {
    payload.dayPrice = null;
    payload.dayUseStartHour = null;
    payload.dayUseEndHour = null;
  }

  // Overnight fields if applicable
  if (mode === "overnight" || mode === "hybrid") {
    payload.nightlyPrice = nightlyPrice === null || nightlyPrice === undefined ? null : Number(nightlyPrice || 0);
    payload.minNights = Number.isFinite(Number(minNights)) ? Number(minNights) : 1;
    payload.maxNights = Number.isFinite(Number(maxNights)) ? Number(maxNights) : 30;
    payload.checkInHour = Number.isFinite(Number(checkInHour)) ? Number(checkInHour) : null;
    payload.checkOutHour = Number.isFinite(Number(checkOutHour)) ? Number(checkOutHour) : null;
  } else {
    payload.nightlyPrice = null;
    payload.minNights = null;
    payload.maxNights = null;
    payload.checkInHour = null;
    payload.checkOutHour = null;
  }

  const listingsCol = collection(db, "listings");
  const docRef = await addDoc(listingsCol, payload);
  return docRef.id;
}


export async function getListingById(id) {
  const ref_ = doc(db, "listings", id);
  const snap = await getDoc(ref_);
  if (!snap.exists()) return null;
  return toListing(snap);
}

// Convenience alias for older imports in EditListing
export { getListingById as fetchListing };

/** ---------- NEW: Update existing listing (used by EditListing) ---------- **/
export async function updateListing(id, updates = {}) {
  if (!id) throw new Error("Missing listing id");
  const me = auth.currentUser || (await ensureAuthReady());
  if (!me) throw new Error("Not signed in");

  const ref_ = doc(db, "listings", id);
  const snap = await getDoc(ref_);
  if (!snap.exists()) throw new Error("Listing not found");
  const cur = snap.data() || {};

  // Client-side guard (also enforce in Security Rules)
  if (cur.ownerUid && cur.ownerUid !== me.uid) {
    throw new Error("You don’t have permission to edit this listing.");
  }

  // Extract helper-only keys
  const {
    newFiles = [],
    deletedPhotoUrls = [],
    onProgress,
    ...rest
  } = updates;

  // Photos: start from existing, remove deleted, then append uploads
  let nextPhotos = Array.isArray(cur.photos) ? cur.photos.slice() : [];
  if (deletedPhotoUrls?.length) {
    const del = new Set(deletedPhotoUrls);
    nextPhotos = nextPhotos.filter((u) => !del.has(u));
  }

  // Upload new files (aggregate progress by bytes)
  const files = Array.isArray(newFiles) ? newFiles : [];
  const totalBytes = files.reduce((s, f) => s + (f?.size || 0), 0) || 1;
  let uploadedSoFar = 0;
  for (const f of files) {
    const { url, bytes } = await uploadListingImage({
      ownerUid: cur.ownerUid || me.uid,
      file: f,
      baseUploaded: uploadedSoFar,
      totalBytes,
      onProgress,
    });
    nextPhotos.push(url);
    uploadedSoFar += bytes;
  }
  onProgress?.(100);

// Normalize arrays like publishListing
const cleanAreas = normalizeStringList(rest.areas, 100);
const cleanAmenities = normalizeStringList(rest.amenities, 200);
const cleanEventTypes = normalizeStringList(rest.goodForEventsTypes, 100);

const patch = {
  ...rest,

  ...(Object.prototype.hasOwnProperty.call(rest, "areas") ? { areas: cleanAreas } : {}),
  ...(Object.prototype.hasOwnProperty.call(rest, "amenities") ? { amenities: cleanAmenities } : {}),
  ...(Object.prototype.hasOwnProperty.call(rest, "goodForEventsTypes")
    ? { goodForEventsTypes: cleanEventTypes }
    : {}),

  photos: nextPhotos,
  updatedAt: serverTimestamp(),
};

// --- Capacity back-compat + split fields ---
if (
  Object.prototype.hasOwnProperty.call(rest, "maxGuestsDay") ||
  Object.prototype.hasOwnProperty.call(rest, "capacity")
) {
  const raw = rest.maxGuestsDay ?? rest.capacity ?? cur.maxGuestsDay ?? cur.capacity ?? 0;
  const n = Number(raw);
  const dayCap = Number.isFinite(n) && n >= 0 ? n : 0;

  patch.maxGuestsDay = dayCap;
  patch.capacity = dayCap; // legacy field
}

if (Object.prototype.hasOwnProperty.call(rest, "maxGuestsOvernight")) {
  const n = Number(rest.maxGuestsOvernight ?? 0);
  patch.maxGuestsOvernight = Number.isFinite(n) && n >= 0 ? n : 0;
}

// --- Approval invariant ---
if (
  Object.prototype.hasOwnProperty.call(rest, "instantBook") ||
  Object.prototype.hasOwnProperty.call(rest, "approvalWindowHours")
) {
  const instant = rest.instantBook ?? cur.instantBook ?? true;
  patch.instantBook = !!instant;

  if (patch.instantBook) {
    patch.approvalWindowHours = null;
  } else {
    const n = Number(rest.approvalWindowHours ?? cur.approvalWindowHours ?? 4);
    patch.approvalWindowHours = Number.isFinite(n) && n > 0 ? n : 4;
  }
}


  // Booking-mode cleanup: ensure unrelated fields are nulled for clean reads
  if (patch.bookingMode) {
    const mode = String(patch.bookingMode || "day").toLowerCase();
    patch.bookingMode = mode;

    if (mode === "overnight") {
      patch.dayPrice = null;
      patch.dayUseStartHour = null;
      patch.dayUseEndHour = null;
    }
    if (mode === "day") {
      patch.nightlyPrice = null;
      patch.minNights = null;
      patch.maxNights = null;
      patch.checkInHour = null;
      patch.checkOutHour = null;
    }
  }

  await updateDoc(ref_, patch);
  return { ok: true };
}

// handy for BookingSuccess fallback
export async function getBookingById(id) {
  const ref_ = doc(db, "bookings", String(id));
  const snap = await getDoc(ref_);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export function watchPublishedListings({ onChange, onError, limitTo = 60 }) {
  const qRef = query(
    collection(db, "listings"),
    where("status", "==", "published"),
    orderBy("publishedAt", "desc"),
    qLimit(limitTo)
  );
  return onSnapshot(
    qRef,
    (snap) => onChange?.(snap.docs.map(toListing)),
    (err) => onError?.(err)
  );
}

export function watchHostListings({ ownerUid, onChange, onError, limitTo = 100 }) {
  const qRef = query(
    collection(db, "listings"),
    where("ownerUid", "==", ownerUid),
    orderBy("updatedAt", "desc"),
    qLimit(limitTo)
  );
  return onSnapshot(
    qRef,
    (snap) => onChange?.(snap.docs.map(toListing)),
    (err) => onError?.(err)
  );
}

/** ====== Host bookings (resilient against missing composite index) ====== **/
export async function fetchHostBookings({
  hostUid,
  mode = null,
  limitTo = 100,
  orderDir = "desc",
} = {}) {
  if (!hostUid) throw new Error("hostUid required");

  // Preferred queries (may require a composite index for createdAt + equality filter)
  const base = [
    collection(db, "bookings"),
    where("hostUid", "==", hostUid),
    orderBy("createdAt", orderDir === "asc" ? "asc" : "desc"),
    qLimit(limitTo),
  ];

  const withMode = [
    collection(db, "bookings"),
    where("hostUid", "==", hostUid),
    where("mode", "==", mode),
    orderBy("createdAt", orderDir === "asc" ? "asc" : "desc"),
    qLimit(limitTo),
  ];

  try {
    const qRef = mode ? query(...withMode) : query(...base);
    const snap = await getDocs(qRef);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    // Missing index? Fall back to an index-free query and sort/filter client-side.
    if (err?.code === "failed-precondition" && /index/i.test(err?.message || "")) {
      const raw = await getDocs(
        query(collection(db, "bookings"), where("hostUid", "==", hostUid))
      );
      let rows = raw.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (mode) rows = rows.filter((r) => r.mode === mode);
      rows.sort((a, b) => {
        const av = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bv = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return orderDir === "asc" ? av - bv : bv - av;
      });
      return rows.slice(0, limitTo);
    }
    throw err;
  }
}

/** ============================ Bookings API =========================== **/
// Per-day document id
export function bookingDocId(listingId, date) {
  return `${listingId}_${date}`;
}

/* ---------- LOCAL (fallback) availability checks ---------- */
async function localCheckAvailability(listingId, date) {
  try {
    const key = bookingDocId(listingId, date);
    const ref_ = doc(db, "bookings", key);
    const snap = await getDoc(ref_);
    // Permission errors flow to catch; otherwise treat any active row as unavailable
    return !snap.exists() || snap.data()?.isActive === false;
  } catch {
    // If rules block the read (e.g., unpublished listing), be conservative
    return false;
  }
}
async function localCheckAvailabilityRange(listingId, startDate, endDateExclusive) {
  const start = String(startDate);
  const end = String(endDateExclusive);

  const bookingsQ = query(
    collection(db, "bookings"),
    where("listingId", "==", listingId),
    where("isActive", "==", true),
    where("date", ">=", start),
    where("date", "<", end),
    qLimit(1)
  );

  const calCol = collection(db, "listings", String(listingId), "calendar");
  const calendarQ = query(
    calCol,
    where(documentId(), ">=", start),
    where(documentId(), "<", end),
    orderBy(documentId(), "asc"),
    qLimit(50)
  );

  const [bSnap, cSnap] = await Promise.all([getDocs(bookingsQ), getDocs(calendarQ)]);

  if (!bSnap.empty) return false;

  // any busy calendar doc makes range unavailable
  let anyCalBusy = false;
  cSnap.forEach((d) => {
    if (isCalendarDocBusy(d.data())) anyCalBusy = true;
  });
  if (anyCalBusy) return false;

  return true;
}


/** ======================= Stripe / RPC Helper ======================== **/
const REQUEST_TIMEOUT_MS = 20000;
function getStripeFnBase() {
  return (
    (typeof import.meta !== "undefined" && import.meta?.env?.VITE_STRIPE_FN) ||
    (typeof process !== "undefined" && process?.env?.REACT_APP_STRIPE_FN) ||
    "https://us-central1-unionhall-1673d.cloudfunctions.net/stripe"
  );
}

// Public (no-auth) RPC — useful for availability checks when user isn’t signed in
async function stripeRpcPublic(action, payload) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
  try {
    // Back-compat param shim(s)
    let body = { action, ...payload };
    if (action === "startCheckoutOvernight" && body.endDate && !body.endDateExclusive) {
      body.endDateExclusive = body.endDate;
    }
    if (action === "checkAvailabilityRange" && body.endDate && !body.endDateExclusive) {
      body.endDateExclusive = body.endDate;
    }

    const r = await fetch(`${getStripeFnBase()}/rpc`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
    const raw = await r.text();
    let data = null;
    try {
      data = raw ? JSON.parse(raw) : null;
    } catch {
      /* non-JSON */
    }
    if (!r.ok) {
      const msg = data?.error || raw || `RPC failed with ${r.status}`;
      throw new Error(msg);
    }
    return data;
  } finally {
    clearTimeout(timer);
  }
}

export async function stripeRpc(action, payload) {
  const me = auth.currentUser || (await ensureAuthReady());
  if (!me) throw new Error("Not signed in");
  const idToken = await me.getIdToken(true);

  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
  try {
    // Back-compat param shim(s)
    let body = { action, ...payload };
    if (action === "startCheckoutOvernight" && body.endDate && !body.endDateExclusive) {
      body.endDateExclusive = body.endDate;
    }
    if (action === "checkAvailabilityRange" && body.endDate && !body.endDateExclusive) {
      body.endDateExclusive = body.endDate;
    }

    const r = await fetch(`${getStripeFnBase()}/rpc`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
    const raw = await r.text();
    let data = null;
    try {
      data = raw ? JSON.parse(raw) : null;
    } catch {
      // non-JSON
    }
    if (!r.ok) {
      const msg = data?.error || raw || `RPC failed with ${r.status}`;
      throw new Error(msg);
    }
    return data;
  } finally {
    clearTimeout(timer);
  }
}

/** ======================= ICS / Calendar RPC Helper ==================== **/
const ICS_TIMEOUT_MS = 20000;

// If you ever want to override base in dev, set VITE_ICS_BASE or REACT_APP_ICS_BASE.
// Otherwise we use relative paths (/api/ics/...) which work through Hosting rewrites.
function getIcsBase() {
  return (
    (typeof import.meta !== "undefined" && import.meta?.env?.VITE_ICS_BASE) ||
    (typeof process !== "undefined" && process?.env?.REACT_APP_ICS_BASE) ||
    "" // relative to current origin
  );
}

// POST helper with auth (mirrors stripeRpc)
async function icsPost(path, payload, { requireAuth = true } = {}) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ICS_TIMEOUT_MS);
  try {
    const headers = { "Content-Type": "application/json" };

    if (requireAuth) {
      const me = auth.currentUser || (await ensureAuthReady());
      if (!me) throw new Error("Not signed in");
      const idToken = await me.getIdToken(true);
      headers.Authorization = `Bearer ${idToken}`;
    }

    const r = await fetch(`${getIcsBase()}${path}`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload || {}),
      signal: ctrl.signal,
    });

    const raw = await r.text();
    let data = null;
    try {
      data = raw ? JSON.parse(raw) : null;
    } catch {
      /* non-JSON */
    }

    if (!r.ok) {
      const msg = data?.error || raw || `ICS RPC failed with ${r.status}`;
      throw new Error(msg);
    }

    return data;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Force-import external ICS into Firestore for a listing.
 * This one MUST be authed (your Cloud Run verifyFirebaseAuth enforces it).
 */
export async function importNowIcs({ listingId }) {
  if (!listingId) throw new Error("listingId required");
  return await icsPost("/api/ics/importNow", { listingId }, { requireAuth: true });
}

/* ---------- Availability (server-first; fallback to local) ---------- */
export async function checkAvailability(listingId, date) {
  try {
    const rpc = auth.currentUser ? stripeRpc : stripeRpcPublic;
    const out = await rpc("checkAvailability", { listingId, date });
    return !!out?.available;
  } catch {
    return await localCheckAvailability(listingId, date);
  }
}

// Accepts endDate or endDateExclusive; treats it as exclusive
export async function checkAvailabilityRange(listingId, startDate, endDateOrExclusive) {
  const endDateExclusive = endDateOrExclusive;
  try {
    const rpc = auth.currentUser ? stripeRpc : stripeRpcPublic;
    const out = await rpc("checkAvailabilityRange", {
      listingId,
      startDate,
      endDateExclusive,
    });
    return !!out?.available;
  } catch {
    return await localCheckAvailabilityRange(listingId, startDate, endDateExclusive);
  }
}

/* ---------- NEW: Batched, cheap availability for Explore grid ---------- */
// Small in-memory cache to avoid repeat queries within a short window
const _availCache = new Map(); // key -> { value, at }
const _AVAIL_TTL_MS = 60_000;
function _cacheGet(key) {
  const hit = _availCache.get(key);
  if (!hit) return undefined;
  if (Date.now() - hit.at > _AVAIL_TTL_MS) {
    _availCache.delete(key);
    return undefined;
  }
  return hit.value;
}
function _cacheSet(key, value) {
  _availCache.set(key, { value, at: Date.now() });
}

// Returns { [listingId]: true|false } for a single day.
// true = available, false = unavailable.
export async function checkAvailabilityManyDay(listingIds, yyyy_mm_dd) {
  const ids = Array.isArray(listingIds) ? listingIds.filter(Boolean) : [];
  if (ids.length === 0 || !yyyy_mm_dd) return {};

  // Serve from cache if we can
  const baseKey = `D|${yyyy_mm_dd}|`;
  const results = {};
  const pending = [];
  for (const id of ids) {
    const k = baseKey + id;
    const v = _cacheGet(k);
    if (typeof v === "boolean") results[id] = v;
    else pending.push(id);
  }
  if (pending.length === 0) return results;

  // Firestore allows at most 10 values in an "in" filter
  const chunks = [];
  for (let i = 0; i < pending.length; i += 10) chunks.push(pending.slice(i, i + 10));

  async function runChunk(chunk) {
    try {
      const qRef = query(
        collection(db, "bookings"),
        where("listingId", "in", chunk),
        where("date", "==", yyyy_mm_dd),
        where("isActive", "==", true),
        qLimit(500) // guardrail
      );
      const snap = await getDocs(qRef);
      // default to available; mark false if any active row exists
      for (const id of chunk) results[id] = true;
      snap.forEach((d) => {
        const lid = d.data()?.listingId;
        if (lid) results[lid] = false;
      });
    } catch (err) {
      // Missing index or unsupported combo? Fall back to 1-by-1 (still cheap for small N)
      if (err?.code === "failed-precondition" && /index/i.test(err?.message || "")) {
        await Promise.all(
          chunk.map(async (id) => {
            const qRef = query(
              collection(db, "bookings"),
              where("listingId", "==", id),
              where("date", "==", yyyy_mm_dd),
              where("isActive", "==", true),
              qLimit(1)
            );
            const qs = await getDocs(qRef);
            results[id] = qs.empty; // empty means available
          })
        );
      } else {
        // On unexpected errors, mark unknowns as available=false (conservative) or null.
        chunk.forEach((id) => {
          results[id] = false;
        });
      }
    }
  }

  for (const c of chunks) {
    // eslint-disable-next-line no-await-in-loop
    await runChunk(c);
  }

  // Cache results
  for (const id of pending) _cacheSet(baseKey + id, !!results[id]);
  return results;
}

// Returns { [listingId]: true|false } for a date span [start, endExclusive)
export async function checkAvailabilityManyRange(
  listingIds,
  startYYYYMMDD,
  endExclusiveYYYYMMDD
) {
  const ids = Array.isArray(listingIds) ? listingIds.filter(Boolean) : [];
  if (ids.length === 0 || !startYYYYMMDD || !endExclusiveYYYYMMDD) return {};

  const baseKey = `R|${startYYYYMMDD}->${endExclusiveYYYYMMDD}|`;
  const results = {};
  const pending = [];
  for (const id of ids) {
    const k = baseKey + id;
    const v = _cacheGet(k);
    if (typeof v === "boolean") results[id] = v;
    else pending.push(id);
  }
  if (pending.length === 0) return results;

  const chunks = [];
  for (let i = 0; i < pending.length; i += 10) chunks.push(pending.slice(i, i + 10));

  async function runChunk(chunk) {
    try {
      const qRef = query(
        collection(db, "bookings"),
        where("listingId", "in", chunk),
        where("isActive", "==", true),
        where("date", ">=", startYYYYMMDD),
        where("date", "<", endExclusiveYYYYMMDD),
        qLimit(1000)
      );
      const snap = await getDocs(qRef);
      for (const id of chunk) results[id] = true; // assume available
      snap.forEach((d) => {
        const lid = d.data()?.listingId;
        if (lid) results[lid] = false; // any busy row makes it unavailable
      });
    } catch (err) {
      if (err?.code === "failed-precondition" && /index/i.test(err?.message || "")) {
        // Fallback: per-id query with range (still 1 per listing, not one per day)
        await Promise.all(
          chunk.map(async (id) => {
            const qRef = query(
              collection(db, "bookings"),
              where("listingId", "==", id),
              where("isActive", "==", true),
              where("date", ">=", startYYYYMMDD),
              where("date", "<", endExclusiveYYYYMMDD),
              qLimit(1)
            );
            const qs = await getDocs(qRef);
            results[id] = qs.empty;
          })
        );
      } else {
        chunk.forEach((id) => {
          results[id] = false;
        });
      }
    }
  }

  for (const c of chunks) {
    // eslint-disable-next-line no-await-in-loop
    await runChunk(c);
  }

  for (const id of pending) _cacheSet(baseKey + id, !!results[id]);
  return results;
}

/* ---------- NEW: One-off range fetch for ListingDetail calendar ---------- */
// Returns array of { listingId, date, isActive, ... } busy rows within [from, toExclusive)
export async function getAvailabilityRangeOnce({
  listingId,
  fromYYYYMMDD,
  toYYYYMMDDExclusive,
}) {
  if (!listingId || !fromYYYYMMDD || !toYYYYMMDDExclusive) return [];

  const bookingsQ = query(
    collection(db, "bookings"),
    where("listingId", "==", listingId),
    where("isActive", "==", true),
    where("date", ">=", fromYYYYMMDD),
    where("date", "<", toYYYYMMDDExclusive),
    orderBy("date", "asc")
  );

  const calCol = collection(db, "listings", String(listingId), "calendar");
  const calendarQ = query(
    calCol,
    where(documentId(), ">=", fromYYYYMMDD),
    where(documentId(), "<", toYYYYMMDDExclusive),
    orderBy(documentId(), "asc")
  );

  const [bSnap, cSnap] = await Promise.all([getDocs(bookingsQ), getDocs(calendarQ)]);

  const bookingRows = bSnap.docs.map((d) => {
    const data = d.data() || {};
    const date = data.date || d.id;
    return { ...data, date, origin: data.origin || "booking" };
  });

  const calendarRows = [];
  cSnap.forEach((docSnap) => {
    const data = docSnap.data() || {};
    if (!isCalendarDocBusy(data)) return;
    const day = data.date || docSnap.id;
    calendarRows.push({
      ...data,
      date: day,
      origin: data.origin || "calendar",
      calendarId: docSnap.id,
    });
  });

  return [...bookingRows, ...calendarRows].sort((a, b) =>
    String(a.date || "").localeCompare(String(b.date || ""))
  );
}


/* ---------- Optional: month-scoped realtime listener (cheaper) ---------- */
export function watchAvailabilityMonth({
  listingId,
  year, // e.g., 2025
  month, // 1-12
  onChange,
  onError,
}) {
  if (!listingId || !year || !month) return () => {};
  const start = ymd(new Date(year, month - 1, 1));
  const toExclusive = ymd(new Date(year, month, 1)); // first day of next month
  const qRef = query(
    collection(db, "bookings"),
    where("listingId", "==", listingId),
    where("isActive", "==", true),
    where("date", ">=", start),
    where("date", "<", toExclusive),
    orderBy("date", "asc")
  );
  return onSnapshot(
    qRef,
    (snap) => onChange?.(snap.docs.map((d) => d.data())),
    (err) => onError?.(err)
  );
}

/** ------------------------- Blocks (day & range) ---------------------- **/
export async function createBooking({
  listingId,
  hostUid,
  guestUid,
  date,
  guests,
  note,
}) {
  const bookingId = bookingDocId(listingId, date);
  const bookingRef = doc(db, "bookings", bookingId);
  const listingRef = doc(db, "listings", listingId);

  await runTransaction(db, async (tx) => {
    const existing = await tx.get(bookingRef);
    if (existing.exists() && existing.data()?.isActive) {
      throw new Error("This date is no longer available.");
    }
    const listingSnap = await tx.get(listingRef);
    if (!listingSnap.exists()) throw new Error("Listing not found.");

    const listing = toListing(listingSnap);
    const price = Number(listing.dayPrice || 0);
    const serviceFee = Number(SERVICE_FEE_USD);
    const total = price + serviceFee;

    tx.set(bookingRef, {
      listingId,
      hostUid: hostUid || listing.ownerUid,
      guestUid,
      date, // YYYY-MM-DD
      guests: Number(guests || 1),
      price,
      serviceFee,
      total,
      note: note || "",
      status: "pending",
      isActive: true,
      type: "booking",
      groupId: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });

  return bookingId;
}

// Create a host block for one date
export async function createBlock({
  listingId,
  hostUid,
  date,
  note = "Host blocked",
  groupId = null,
}) {
  const bookingId = bookingDocId(listingId, date);
  const bookingRef = doc(db, "bookings", bookingId);
  const listingRef = doc(db, "listings", listingId);

  await runTransaction(db, async (tx) => {
    const existing = await tx.get(bookingRef);
    if (existing.exists() && existing.data()?.isActive) {
      throw new Error("That date is already unavailable.");
    }
    const listingSnap = await tx.get(listingRef);
    if (!listingSnap.exists()) throw new Error("Listing not found.");

    tx.set(bookingRef, {
      listingId,
      hostUid,
      guestUid: hostUid, // self-block
      date,
      guests: 1,
      price: 0,
      serviceFee: 0,
      total: 0,
      note,
      status: "blocked",
      isActive: true,
      type: "block",
      groupId: groupId || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });

  return bookingId;
}

// Range block: [start, end) end exclusive. Writes all days atomically.
export async function createBlockRange({
  listingId,
  hostUid,
  startDate, // YYYY-MM-DD (inclusive)
  endDateExclusive, // YYYY-MM-DD (exclusive)
  note = "Host blocked",
  groupId = randomGroupId("blk"),
}) {
  if (!listingId || !hostUid || !startDate || !endDateExclusive) {
    throw new Error("listingId, hostUid, startDate, endDateExclusive are required");
  }

  const ok = await checkAvailabilityRange(listingId, startDate, endDateExclusive);
  if (!ok) throw new Error("One or more dates in this range are already unavailable.");

  const batch = writeBatch(db);
  for (const ds of iterDatesYYYYMMDD(startDate, endDateExclusive)) {
    const id = bookingDocId(listingId, ds);
    const ref_ = doc(db, "bookings", id);
    batch.set(ref_, {
      listingId,
      hostUid,
      guestUid: hostUid,
      date: ds,
      guests: 1,
      price: 0,
      serviceFee: 0,
      total: 0,
      note,
      status: "blocked",
      isActive: true,
      type: "block",
      groupId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
  await batch.commit();
  return { groupId };
}

// Unblock a single date
export async function unblockDate({ listingId, date }) {
  const bookingId = bookingDocId(listingId, date);
  const ref_ = doc(db, "bookings", bookingId);
  const snap = await getDoc(ref_);
  if (!snap.exists()) return;
  await updateDoc(ref_, {
    status: "canceled",
    isActive: false,
    updatedAt: serverTimestamp(),
    note: `${snap.data()?.note || "Host blocked"} • unblocked`,
  });
}

// Unblock an entire span by groupId
export async function unblockSpanByGroupId({ listingId, groupId }) {
  if (!listingId || !groupId) throw new Error("listingId and groupId required");
  const qRef = query(
    collection(db, "bookings"),
    where("listingId", "==", listingId),
    where("groupId", "==", groupId),
    where("isActive", "==", true),
    where("status", "==", "blocked")
  );
  const qs = await getDocs(qRef);
  if (qs.empty) return { count: 0 };

  const batch = writeBatch(db);
  let count = 0;
  qs.forEach((d) => {
    batch.update(d.ref, {
      status: "canceled",
      isActive: false,
      updatedAt: serverTimestamp(),
      note: `${d.data()?.note || "Host blocked"} • unblocked (span)`,
    });
    count += 1;
  });
  await batch.commit();
  return { count };
}

/** --------------------- Calendar (ICS) helpers ----------------------- **/

// Treat any calendar doc (listings/{id}/calendar/{YYYY-MM-DD}) as busy
// unless it is explicitly marked open / inactive.
function isCalendarDocBusy(c) {
  if (!c) return false;

  // Explicitly open / inactive
  if (c.isActive === false) return false;

  const rawStatus = String(c.status || c.type || "").toLowerCase();

  // Things that are clearly NOT busy
  if (["open", "available", "free", "canceled", "cancelled"].includes(rawStatus)) {
    return false;
  }

  // Anything else present (Airbnb, VRBO, manual holds, etc.) = busy
  return true;
}


/** ---------------------- Availability watchers ----------------------- **/
// NOTE: Uses end-exclusive semantics internally; accepts old end-inclusive for compatibility.
// NOW: merges Firestore bookings *and* listings/{listingId}/calendar (Airbnb/VRBO ICS) docs.
export function watchAvailabilityRange({
  listingId,
  fromYYYYMMDD,
  toYYYYMMDD, // historical param (treated as inclusive)
  toYYYYMMDDExclusive, // preferred param
  onChange,
  onError,
}) {
  if (!listingId || !fromYYYYMMDD || (!toYYYYMMDD && !toYYYYMMDDExclusive))
    return () => {};

  const endExclusive = toYYYYMMDDExclusive || endExclusiveFromEndDate(toYYYYMMDD);

  // State from both sources
  let bookingRows = [];
  let calendarRows = [];

  const emit = () => {
    if (!onChange) return;

    // Merge and sort by date so existing UIs that just look at `.date` still work.
    const merged = [...bookingRows, ...calendarRows].sort((a, b) => {
      const da = String(a.date || "");
      const db = String(b.date || "");
      return da.localeCompare(db);
    });

    onChange(merged);
  };

  // --- 1) Existing bookings watcher (host blocks + guest bookings) ---
  const bookingsQuery = query(
    collection(db, "bookings"),
    where("listingId", "==", listingId),
    where("isActive", "==", true), // required by rules for public/owner preview
    where("date", ">=", fromYYYYMMDD),
    where("date", "<", endExclusive),
    orderBy("date", "asc")
  );

  const unsubBookings = onSnapshot(
    bookingsQuery,
    (snap) => {
      bookingRows = snap.docs.map((d) => {
        const data = d.data() || {};
        // Ensure a `date` field is present and add an origin flag
        const date = data.date || d.id; // booking docs already have `date`, but keep it safe
        return { ...data, date, origin: data.origin || "booking" };
      });
      emit();
    },
    (err) => onError?.(err)
  );

  // --- 2) NEW: calendar watcher for Airbnb/VRBO/ICS holds ---
  const calCol = collection(db, "listings", String(listingId), "calendar");

  const calQuery = query(
    calCol,
    // Query by document id (YYYY-MM-DD) using documentId()
    where(documentId(), ">=", fromYYYYMMDD),
    where(documentId(), "<", endExclusive),
    orderBy(documentId(), "asc")
  );


  const unsubCalendar = onSnapshot(
    calQuery,
    (snap) => {
      const rows = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        if (!isCalendarDocBusy(data)) return; // skip open/cleared days

        const day = data.date || docSnap.id; // ensure we always have a YYYY-MM-DD
        rows.push({
          ...data,
          date: day,
          origin: data.origin || "calendar",
          calendarId: docSnap.id,
        });
      });
      calendarRows = rows;
      emit();
    },
    (err) => onError?.(err)
  );

  return () => {
    try {
      unsubBookings();
    } catch {}
    try {
      unsubCalendar();
    } catch {}
  };
}

// Host/admin view: watch ALL rows in a range for a listing (no isActive filter).
// Use this only in authenticated dashboards where the user is host/admin.
export function watchBookingsRangeAll({
  listingId,
  fromYYYYMMDD,
  toYYYYMMDD,
  toYYYYMMDDExclusive,
  onChange,
  onError,
}) {
  if (!listingId || !fromYYYYMMDD || (!toYYYYMMDD && !toYYYYMMDDExclusive))
    return () => {};
  const endExclusive = toYYYYMMDDExclusive || endExclusiveFromEndDate(toYYYYMMDD);
  const qRef = query(
    collection(db, "bookings"),
    where("listingId", "==", listingId),
    where("date", ">=", fromYYYYMMDD),
    where("date", "<", endExclusive),
    orderBy("date", "asc")
  );
  return onSnapshot(
    qRef,
    (snap) => onChange?.(snap.docs.map((d) => d.data())),
    (err) => onError?.(err)
  );
}

/** ===================== Host Verification API ========================= **/
export async function getHostVerificationStatus({ uid }) {
  const refDoc = doc(db, "verifications", uid);
  const snap = await getDoc(refDoc);
  return snap.exists() ? snap.data() : null;
}

export async function submitIdAndSelfie({ uid, idFront, idBack, selfie, onProgress }) {
  if (!uid) throw new Error("uid required");
  const files = [idFront, idBack, selfie].filter(Boolean);
  if (!files.length) throw new Error("No ID/selfie files provided");

  await ensureAuthReady();

  const total = files.length;
  const now = Date.now();
  let i = 0;
  const urls = {};

  for (const f of files) {
    const kind = f === idFront ? "front" : f === idBack ? "back" : "selfie";
    const ext = (f.name?.split(".").pop() || "jpg").toLowerCase();
    const storageRef = ref(storage, `verifications/${uid}/id/${kind}-${now}.${ext}`);
    const { url } = await uploadOneWithAggregateProgress({
      storageRef,
      file: f,
      i,
      total,
      onProgress,
    });
    urls[kind] = url;
    i += 1;
  }

  const refDoc = doc(db, "verifications", uid);
  await setDoc(
    refDoc,
    {
      idVerification: {
        status: "pending",
        submittedAt: serverTimestamp(),
        note: "",
        urls,
      },
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  onProgress?.(100);
  return { ok: true };
}

export async function submitRightToList({
  uid,
  ownershipType,
  docFile,
  consentFile,
  onProgress,
}) {
  if (!uid) throw new Error("uid required");
  if (!ownershipType) throw new Error("ownershipType required");

  await ensureAuthReady();

  const files = [docFile, consentFile].filter(Boolean);
  if (!files.length) throw new Error("No Right-to-List file provided");

  const total = files.length;
  const now = Date.now();
  let i = 0;
  const urls = {};

  for (const f of files) {
    const key = f === consentFile ? "consent" : "doc";
    const ext = (f.name?.split(".").pop() || "pdf").toLowerCase();
    const storageRef = ref(storage, `verifications/${uid}/rights/${key}-${now}.${ext}`);
    const { url } = await uploadOneWithAggregateProgress({
      storageRef,
      file: f,
      i,
      total,
      onProgress,
    });
    urls[key] = url;
    i += 1;
  }

  const refDoc = doc(db, "verifications", uid);
  await setDoc(
    refDoc,
    {
      rightToList: {
        status: "pending",
        type: ownershipType,
        submittedAt: serverTimestamp(),
        note: "",
        urls,
      },
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  onProgress?.(100);
  return { ok: true };
}

/** ========== Admin watchers (VerificationQueue) ========== **/
export function watchPendingIdVerifications({ onChange, onError, limitTo = 100 }) {
  const qRef = query(
    collection(db, "verifications"),
    where("idVerification.status", "==", "pending"),
    orderBy("idVerification.submittedAt", "desc"),
    qLimit(limitTo)
  );
  return onSnapshot(
    qRef,
    (snap) =>
      onChange?.(
        snap.docs.map((d) => {
          const v = (d.data() || {}).idVerification || {};
          return {
            uid: d.id,
            track: "idVerification",
            status: v.status || "unverified",
            submittedAt: v.submittedAt || null,
            note: v.note || "",
            urls: v.urls || {},
          };
        })
      ),
    (err) => onError?.(err)
  );
}

export function watchPendingRightToList({ onChange, onError, limitTo = 100 }) {
  const qRef = query(
    collection(db, "verifications"),
    where("rightToList.status", "==", "pending"),
    orderBy("rightToList.submittedAt", "desc"),
    qLimit(limitTo)
  );
  return onSnapshot(
    qRef,
    (snap) =>
      onChange?.(
        snap.docs.map((d) => {
          const v = (d.data() || {}).rightToList || {};
          return {
            uid: d.id,
            track: "rightToList",
            status: v.status || "unverified",
            submittedAt: v.submittedAt || null,
            type: v.type || null,
            note: v.note || "",
            urls: v.urls || {},
          };
        })
      ),
    (err) => onError?.(err)
  );
}

/** ========== Admin review helpers ========== **/
export async function reviewIdVerification({ uid, status, note }) {
  if (!uid || !status) throw new Error("uid and status required");
  const adminUid = auth.currentUser?.uid || "admin";
  const payload = {
    "idVerification.status": status,
    "idVerification.reviewedAt": serverTimestamp(),
    "idVerification.reviewedBy": adminUid,
    "idVerification.note": note || "",
    updatedAt: serverTimestamp(),
  };
  if (status === "verified") {
    payload["idVerification.verifiedAt"] = serverTimestamp();
  }
  await updateDoc(doc(db, "verifications", uid), payload);
  return { ok: true };
}

export async function reviewRightToList({ uid, status, note }) {
  if (!uid || !status) throw new Error("uid and status required");
  const adminUid = auth.currentUser?.uid || "admin";
  const payload = {
    "rightToList.status": status,
    "rightToList.reviewedAt": serverTimestamp(),
    "rightToList.reviewedBy": adminUid,
    "rightToList.note": note || "",
    updatedAt: serverTimestamp(),
  };
  if (status === "verified") {
    payload["rightToList.verifiedAt"] = serverTimestamp();
  }
  await updateDoc(doc(db, "verifications", uid), payload);
  return { ok: true };
}

export async function setVerificationStatus({ uid, track, status, note }) {
  if (!uid || !track || !status) throw new Error("uid, track, and status required");
  const adminUid = auth.currentUser?.uid || "admin";
  const path = track === "idVerification" ? "idVerification" : "rightToList";
  const updates = {
    [`${path}.status`]: status,
    [`${path}.reviewedAt`]: serverTimestamp(),
    [`${path}.reviewedBy`]: adminUid,
    [`${path}.note`]: note || "",
    updatedAt: serverTimestamp(),
  };
  if (status === "verified") {
    updates[`${path}.verifiedAt`] = serverTimestamp();
  }
  await updateDoc(doc(db, "verifications", uid), updates);
  return { ok: true };
}

/** ============================ Messaging API =========================== **
 * threads/{threadId}
 *   - participants: { [uid]: true }
 *   - participantUids: [hostUid, guestUid]
 *   - hostUid, guestUid, listingId, bookingId?
 *   - listingTitle?, listingPhoto?
 *   - lastMsg: { text, by, at }
 *   - lastAt: Timestamp
 *   - unread: { [uid]: number }
 *   - createdAt
 *
 * threads/{threadId}/messages/{msgId}
 *   - by, text?, imageUrl?, at
 ** ===================================================================== **/

function makeThreadId({ listingId, hostUid, guestUid }) {
  return `${listingId}__${hostUid}__${guestUid}`;
}

export async function createOrGetInquiryThread({
  listingId,
  hostUid,
  guestUid,
  bookingId = null,
  listingTitle,
  listingPhoto,
}) {
  if (!listingId || !hostUid || !guestUid) throw new Error("Missing ids");
  await ensureAuthReady(); // ensure rules pass (participants-only)

  const tid = makeThreadId({ listingId, hostUid, guestUid });
  const tref = doc(db, "threads", tid);

  const base = {
    participants: { [hostUid]: true, [guestUid]: true },
    participantUids: [hostUid, guestUid],
    hostUid,
    guestUid,
    listingId,
    bookingId: bookingId || null,
    listingTitle: listingTitle || null,
    listingPhoto: listingPhoto || null,
    lastMsg: null,
    lastAt: serverTimestamp(),
    unread: { [hostUid]: 0, [guestUid]: 0 },
    createdAt: serverTimestamp(),
  };

  const snap = await getDoc(tref);

  if (!snap.exists()) {
    await setDoc(tref, base);
  } else {
    const patch = { lastAt: serverTimestamp() };
    if (bookingId != null && !snap.data()?.bookingId) patch.bookingId = bookingId;
    if (listingTitle != null) patch.listingTitle = listingTitle;
    if (listingPhoto != null) patch.listingPhoto = listingPhoto;
    if (Object.keys(patch).length) await updateDoc(tref, patch);
  }

  const fresh = await getDoc(tref);
  return { id: tid, ...fresh.data() };
}

export function watchThreadsForUser({ uid, onChange, onError }) {
  let unsub = () => {};
  let closed = false;

  const start = (userUid) => {
    if (closed || !userUid) return () => {};
    const qRef = query(
      collection(db, "threads"),
      where("participantUids", "array-contains", userUid),
      orderBy("lastAt", "desc"),
      qLimit(50)
    );
    unsub = onSnapshot(
      qRef,
      (qs) => {
        const rows = [];
        qs.forEach((d) => rows.push({ id: d.id, ...d.data() })); // preserve existing shape
        onChange?.(rows);
      },
      (err) => onError?.(err)
    );
    return unsub;
  };

  if (uid) {
    start(uid);
  } else if (auth.currentUser?.uid) {
    start(auth.currentUser.uid);
  } else {
    const offAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        offAuth();
        start(user.uid);
      }
    });
    unsub = () => {
      closed = true;
      offAuth();
    };
  }

  return () => {
    closed = true;
    try {
      unsub();
    } catch {}
  };
}

export function watchMessages({ threadId, onChange, onError }) {
  let unsub = () => {};
  let stopped = false;

  const start = async () => {
    await ensureAuthReady(); // participants-only rules
    if (stopped) return;
    const qRef = query(
      collection(db, "threads", threadId, "messages"),
      orderBy("at", "asc"),
      qLimit(200)
    );
    unsub = onSnapshot(
      qRef,
      (qs) => {
        const rows = [];
        qs.forEach((d) => rows.push({ id: d.id, ...d.data() }));
        onChange?.(rows);
      },
      (err) => onError?.(err)
    );
  };

  start();

  return () => {
    stopped = true;
    try {
      unsub();
    } catch {}
  };
}

export async function sendMessage({ threadId, text }) {
  const me = auth.currentUser;
  if (!me) throw new Error("Not signed in");
  // ✅ fix: trim before length check so whitespace-only messages don't post
  const trimmed = String(text ?? "").trim().slice(0, 4000);
  if (!trimmed) return;

  const tref = doc(db, "threads", threadId);
  const tSnap = await getDoc(tref);
  if (!tSnap.exists()) throw new Error("Thread not found");
  const t = tSnap.data() || {};
  const participants = Object.keys(t.participants || {});
  const others = participants.filter((u) => u !== me.uid);
  const ts = serverTimestamp();

  const batch = writeBatch(db);

  // 1) Add the message
  const mref = doc(collection(db, "threads", threadId, "messages"));
  batch.set(mref, { by: me.uid, text: trimmed, at: ts });

  // 2) Update thread summary + atomically increment others' unread
  const short = trimmed.slice(0, 140);
  const patch = {
    lastAt: ts,
    lastMsg: { text: short, by: me.uid, at: ts },
  };
  for (const uid of others) patch[`unread.${uid}`] = increment(1);
  batch.update(tref, patch);

  await batch.commit();
}

const _markInFlight = new Set();
export async function markThreadRead({ threadId, uid }) {
  const key = `${threadId}|${uid}`;
  if (_markInFlight.has(key)) return;
  _markInFlight.add(key);
  try {
    const tref = doc(db, "threads", threadId);
    await updateDoc(tref, { [`unread.${uid}`]: 0 });
  } finally {
    _markInFlight.delete(key);
  }
}
